import confetti from 'canvas-confetti';
import { Clickable, Col, Text } from 'components';
import { Api } from 'lib';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

const COLOR_WHITE = '#FFFFFF';
const COLOR_DARK_PURPLE = '#291845';
const COLOR_LIGHT_GREY = '#D7D7D7';

const PLACEMENT_KEYS = {
  1: {
    heading: 'gloryBoard.weeklyWinnerHeadingFirst',
    achievement: 'gloryBoard.weeklyWinnerAchievementFirst',
    supporting: 'gloryBoard.weeklyWinnerSupportingFirst'
  },
  2: {
    heading: 'gloryBoard.weeklyWinnerHeadingSecond',
    achievement: 'gloryBoard.weeklyWinnerAchievementSecond',
    supporting: 'gloryBoard.weeklyWinnerSupportingSecond'
  },
  3: {
    heading: 'gloryBoard.weeklyWinnerHeadingThird',
    achievement: 'gloryBoard.weeklyWinnerAchievementThird',
    supporting: 'gloryBoard.weeklyWinnerSupportingThird'
  }
};

const CONFETTI_DURATION_MS = 4000;

/**
 * Over-the-top, full-screen confetti celebration for GloryBoard's weekly top 3 — the fan-facing
 * web counterpart to the mobile app's GloryBoardWeeklyWinnerCelebration, same trigger and same
 * dismiss mechanism.
 *
 * Detects its own trigger: any unread `gloryBoardWeeklyWinner` notification already in Redux state
 * (loaded at login via INITIAL_DATA, or live via the NEW_NOTIFICATION socket event — both already
 * wired for every other notification type) is this celebration's "pending" signal, there is no
 * separate flag. Dismissing it marks that one notification read via the shared
 * /notifications/markOneRead endpoint (the same one the mobile app uses — CMS's own Notifications
 * panel only has a mark-ALL-read endpoint, which would wrongly clear unrelated unread items too).
 *
 * Mounted once, globally, in containers/User.js alongside the existing <Notifications /> bell —
 * the win can be waiting for a fan before they open that panel, since it is driven by a push/data
 * that may have arrived before this session started.
 */
const GloryBoardWeeklyWinnerCelebration = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const confettiInstanceRef = useRef(null);
  const dismissingRef = useRef(false);

  const profileName = useSelector((state) => state.user?.name || state.user?.username || '');
  const notifications = useSelector((state) => state.notifications);

  const pendingWinner = useMemo(() => {
    return (
      (notifications || []).find(
        (n) => n.type === 'gloryBoardWeeklyWinner' && !n.read && n.rank
      ) || null
    );
  }, [notifications]);

  const [activeWinner, setActiveWinner] = useState(null);

  // Latches onto the first pending winner it sees and holds it even after the notification is
  // marked read out from under it on dismiss — otherwise `pendingWinner` would go null mid-close
  // and swap the content before the fan finishes reading it.
  useEffect(() => {
    if (pendingWinner && !activeWinner && !dismissingRef.current) {
      setActiveWinner(pendingWinner);
    }
  }, [pendingWinner, activeWinner]);

  useEffect(() => {
    if (!activeWinner || !canvasRef.current) {
      return;
    }

    const instance = confetti.create(canvasRef.current, { resize: true, useWorker: true });
    confettiInstanceRef.current = instance;

    const end = Date.now() + CONFETTI_DURATION_MS;
    const colors = ['#FFD700', '#FF6B6B', '#4FD1C5', '#F687B3', '#FFFFFF'];

    const frame = () => {
      instance({ particleCount: 4, angle: 60, spread: 70, origin: { x: 0, y: 0.6 }, colors });
      instance({ particleCount: 4, angle: 120, spread: 70, origin: { x: 1, y: 0.6 }, colors });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();

    instance({ particleCount: 120, spread: 100, origin: { y: 0.5 }, colors });

    return () => {
      instance.reset();
      confettiInstanceRef.current = null;
    };
  }, [activeWinner]);

  const handleDismiss = () => {
    if (!activeWinner || dismissingRef.current) {
      return;
    }
    dismissingRef.current = true;

    // Mark read in Redux immediately — without this, dismissing clears `activeWinner` but the
    // notification stays `read:false` in state, so the latch effect above sees `pendingWinner`
    // still set and re-opens the celebration right back on the next render.
    dispatch({ type: 'MARK_ONE_NOTIFICATION_READ', payload: { notifId: activeWinner._id } });
    Api.post('/notifications/markOneRead', { notifId: activeWinner._id }, null, { silent: true });

    setActiveWinner(null);
    dismissingRef.current = false;
  };

  if (!activeWinner?.rank) {
    return null;
  }

  const placementKeys = PLACEMENT_KEYS[activeWinner.rank];
  const heading = t(placementKeys.heading, { name: profileName });
  const achievement = t(placementKeys.achievement);
  const supporting = t(placementKeys.supporting);

  return (
    <Col
      noFlex
      pos="fixed"
      top="0"
      left="0"
      wid="100vw"
      ht="100vh"
      zIndex="9999"
      centerAll
      style={{ backgroundColor: 'rgba(17, 20, 45, 0.7)' }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none'
        }}
      />

      <Col
        noFlex
        center
        centerAll
        gap="16px"
        pad="40px 32px"
        wid="min(420px, 90vw)"
        hasRadius="22px"
        style={{
          backgroundColor: 'rgba(41, 24, 69, 0.85)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)'
        }}
      >
        <Text size="22px" weight={600} col={COLOR_WHITE} center>
          {heading}
        </Text>
        <Text b1 med col={COLOR_WHITE} center>
          {achievement}
        </Text>
        <Text b2 col={COLOR_LIGHT_GREY} center>
          {supporting}
        </Text>
        <Clickable onClick={handleDismiss}>
          <Col
            noFlex
            pad="10px 28px"
            hasRadius="100px"
            style={{ backgroundColor: COLOR_WHITE, marginTop: '8px' }}
          >
            <Text b1 bold col={COLOR_DARK_PURPLE}>
              {t('gloryBoard.close')}
            </Text>
          </Col>
        </Clickable>
      </Col>
    </Col>
  );
};

export default GloryBoardWeeklyWinnerCelebration;
